import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { showToast } from '../utils/toast';
import { Order, CartItem, Address } from '../types';

export function useOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Fetch orders
    const fetchOrders = async () => {
      try {
        // Get orders
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select(`
            id,
            total,
            status,
            payment_method,
            payment_status,
            shipping_address,
            order_date,
            delivery_date,
            created_at,
            updated_at
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (ordersError) throw ordersError;

        // Get order items for each order
        const orderIds = ordersData.map(order => order.id);
        
        const { data: orderItemsData, error: orderItemsError } = await supabase
          .from('order_items')
          .select(`
            id,
            order_id,
            product_id,
            product_name,
            quantity,
            price,
            prescription_required,
            prescription_id
          `)
          .in('order_id', orderIds);

        if (orderItemsError) throw orderItemsError;

        // Map order items to their respective orders
        const formattedOrders: Order[] = ordersData.map(order => {
          const items = orderItemsData
            .filter(item => item.order_id === order.id)
            .map(item => ({
              productId: item.product_id,
              product: {
                id: item.product_id,
                name: item.product_name,
                price: item.price,
                // These are placeholders since we don't have the full product data
                category: '',
                description: '',
                images: [],
                inStock: true,
                stockCount: 0,
                prescriptionRequired: item.prescription_required,
                manufacturer: '',
                rating: 0,
                reviews: 0,
                tags: []
              },
              quantity: item.quantity,
              prescriptionUploaded: !!item.prescription_id,
              prescriptionId: item.prescription_id
            }));

          return {
            id: order.id,
            userId: user.id,
            items: items as CartItem[],
            total: order.total,
            status: order.status,
            paymentMethod: order.payment_method,
            paymentStatus: order.payment_status,
            shippingAddress: order.shipping_address as Address,
            orderDate: order.order_date,
            deliveryDate: order.delivery_date
          };
        });

        setOrders(formattedOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
        showToast.system.error('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();

    // Subscribe to order status changes
    const orderSubscription = supabase
      .channel('orders_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'orders',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        // Update the order in state
        setOrders(prev => prev.map(order => 
          order.id === payload.new.id
            ? { 
                ...order, 
                status: payload.new.status,
                paymentStatus: payload.new.payment_status,
                deliveryDate: payload.new.delivery_date,
                shippingAddress: payload.new.shipping_address
              }
            : order
        ));

        // Show toast notification for status change
        if (payload.old.status !== payload.new.status) {
          const statusMessages = {
            confirmed: 'Your order has been confirmed!',
            processing: 'Your order is being processed',
            shipped: 'Your order has been shipped!',
            delivered: 'Your order has been delivered!',
            cancelled: 'Your order has been cancelled'
          };

          const message = statusMessages[payload.new.status as keyof typeof statusMessages];
          if (message) {
            showToast.shop[payload.new.status](payload.new.id.substring(0, 8));
          }
        }
      })
      .subscribe();

    // Cleanup subscription
    return () => {
      supabase.removeChannel(orderSubscription);
    };
  }, [user]);

  const createOrder = async (orderData: Omit<Order, 'id'>) => {
    if (!user) {
      showToast.auth.authError('Please sign in to place an order');
      throw new Error('Not authenticated');
    }

    try {
      // Insert the order
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          total: orderData.total,
          status: orderData.status,
          payment_method: orderData.paymentMethod,
          payment_status: orderData.paymentStatus,
          shipping_address: orderData.shippingAddress,
          order_date: orderData.orderDate || new Date().toISOString()
        })
        .select('id')
        .single();

      if (orderError) throw orderError;

      // Insert order items
      const orderItems = orderData.items.map(item => ({
        order_id: orderData.id,
        product_id: item.productId,
        product_name: item.product.name,
        quantity: item.quantity,
        price: item.product.price,
        prescription_required: item.product.prescriptionRequired,
        prescription_id: item.prescriptionId
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      showToast.shop.orderPlaced(orderData.id.substring(0, 8));
      
      return orderData.id;
    } catch (error) {
      console.error('Error creating order:', error);
      showToast.system.error('Failed to place order');
      throw error;
    }
  };

  const cancelOrder = async (orderId: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to cancel an order');
      throw new Error('Not authenticated');
    }

    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', orderId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      showToast.shop.orderCancelled(orderId.substring(0, 8));
      
      // Order status will be updated via subscription
    } catch (error) {
      console.error('Error cancelling order:', error);
      showToast.system.error('Failed to cancel order');
      throw error;
    }
  };

  return {
    orders,
    loading,
    createOrder,
    cancelOrder
  };
}